import type {
  BytecodeInstruction,
  ClassInfo,
  FieldInfo,
  MethodInfo,
} from "./types";

type ConstantPoolEntry =
  | { tag: 1; value: string }
  | { tag: 3 | 4; value: number }
  | { tag: 5 | 6; value: bigint }
  | { tag: 7 | 8 | 16 | 19 | 20; index: number }
  | { tag: 9 | 10 | 11 | 12 | 17 | 18; first: number; second: number }
  | { tag: 15; kind: number; index: number }
  | null;

class Reader {
  private offset = 0;

  constructor(private readonly view: DataView) {}

  get position() {
    return this.offset;
  }

  u1() {
    this.ensure(1);
    return this.view.getUint8(this.offset++);
  }

  u2() {
    this.ensure(2);
    const value = this.view.getUint16(this.offset, false);
    this.offset += 2;
    return value;
  }

  u4() {
    this.ensure(4);
    const value = this.view.getUint32(this.offset, false);
    this.offset += 4;
    return value;
  }

  u8() {
    const high = BigInt(this.u4());
    const low = BigInt(this.u4());
    return (high << BigInt(32)) | low;
  }

  bytes(length: number) {
    this.ensure(length);
    const bytes = new Uint8Array(
      this.view.buffer,
      this.view.byteOffset + this.offset,
      length,
    );
    this.offset += length;
    return bytes;
  }

  skip(length: number) {
    this.ensure(length);
    this.offset += length;
  }

  private ensure(length: number) {
    if (this.offset + length > this.view.byteLength) {
      throw new Error("Unexpected end of class file");
    }
  }
}

const CLASS_ACCESS: Record<number, string> = {
  0x0001: "public",
  0x0010: "final",
  0x0020: "super",
  0x0200: "interface",
  0x0400: "abstract",
  0x1000: "synthetic",
  0x2000: "annotation",
  0x4000: "enum",
  0x8000: "module",
};

const MEMBER_ACCESS: Record<number, string> = {
  0x0001: "public",
  0x0002: "private",
  0x0004: "protected",
  0x0008: "static",
  0x0010: "final",
  0x0020: "synchronized",
  0x0040: "bridge",
  0x0080: "varargs",
  0x0100: "native",
  0x0400: "abstract",
  0x0800: "strict",
  0x1000: "synthetic",
  0x4000: "enum",
};

function accessFlags(flags: number, table: Record<number, string>) {
  return Object.entries(table)
    .filter(([bit]) => (flags & Number(bit)) !== 0)
    .map(([, name]) => name);
}

function javaVersion(major: number) {
  if (major <= 44) return "Pre-Java 1.1";
  if (major <= 48) return `Java 1.${major - 44}`;
  return `Java ${major - 44}`;
}

function decodeModifiedUtf8(bytes: Uint8Array) {
  let value = "";
  for (let index = 0; index < bytes.length; ) {
    const first = bytes[index++];
    if ((first & 0x80) === 0) {
      value += String.fromCharCode(first);
      continue;
    }

    if ((first & 0xe0) === 0xc0 && index < bytes.length) {
      const second = bytes[index++];
      if ((second & 0xc0) !== 0x80) {
        value += "\ufffd";
        continue;
      }
      value += String.fromCharCode(
        ((first & 0x1f) << 6) | (second & 0x3f),
      );
      continue;
    }

    if ((first & 0xf0) === 0xe0 && index + 1 < bytes.length) {
      const second = bytes[index++];
      const third = bytes[index++];
      if ((second & 0xc0) !== 0x80 || (third & 0xc0) !== 0x80) {
        value += "\ufffd";
        continue;
      }
      value += String.fromCharCode(
        ((first & 0x0f) << 12) |
          ((second & 0x3f) << 6) |
          (third & 0x3f),
      );
      continue;
    }

    value += "\ufffd";
  }
  return value;
}

function descriptorType(raw: string): string {
  let index = 0;
  const read = (): string => {
    let dimensions = 0;
    while (raw[index] === "[") {
      dimensions += 1;
      index += 1;
    }
    const marker = raw[index++];
    const primitives: Record<string, string> = {
      B: "byte",
      C: "char",
      D: "double",
      F: "float",
      I: "int",
      J: "long",
      S: "short",
      Z: "boolean",
      V: "void",
    };
    let value = primitives[marker] ?? marker;
    if (marker === "L") {
      const end = raw.indexOf(";", index);
      value = raw.slice(index, end < 0 ? raw.length : end).replaceAll("/", ".");
      index = end < 0 ? raw.length : end + 1;
    }
    return value + "[]".repeat(dimensions);
  };

  if (!raw.startsWith("(")) return read();
  index = 1;
  const args: string[] = [];
  while (index < raw.length && raw[index] !== ")") args.push(read());
  index += 1;
  return `(${args.join(", ")}) → ${read()}`;
}

function skipAttributes(reader: Reader, count: number) {
  for (let i = 0; i < count; i += 1) {
    reader.u2();
    reader.skip(reader.u4());
  }
}

const OPCODE_NAMES = [
  "nop",
  "aconst_null",
  "iconst_m1",
  "iconst_0",
  "iconst_1",
  "iconst_2",
  "iconst_3",
  "iconst_4",
  "iconst_5",
  "lconst_0",
  "lconst_1",
  "fconst_0",
  "fconst_1",
  "fconst_2",
  "dconst_0",
  "dconst_1",
  "bipush",
  "sipush",
  "ldc",
  "ldc_w",
  "ldc2_w",
  "iload",
  "lload",
  "fload",
  "dload",
  "aload",
  "iload_0",
  "iload_1",
  "iload_2",
  "iload_3",
  "lload_0",
  "lload_1",
  "lload_2",
  "lload_3",
  "fload_0",
  "fload_1",
  "fload_2",
  "fload_3",
  "dload_0",
  "dload_1",
  "dload_2",
  "dload_3",
  "aload_0",
  "aload_1",
  "aload_2",
  "aload_3",
  "iaload",
  "laload",
  "faload",
  "daload",
  "aaload",
  "baload",
  "caload",
  "saload",
  "istore",
  "lstore",
  "fstore",
  "dstore",
  "astore",
  "istore_0",
  "istore_1",
  "istore_2",
  "istore_3",
  "lstore_0",
  "lstore_1",
  "lstore_2",
  "lstore_3",
  "fstore_0",
  "fstore_1",
  "fstore_2",
  "fstore_3",
  "dstore_0",
  "dstore_1",
  "dstore_2",
  "dstore_3",
  "astore_0",
  "astore_1",
  "astore_2",
  "astore_3",
  "iastore",
  "lastore",
  "fastore",
  "dastore",
  "aastore",
  "bastore",
  "castore",
  "sastore",
  "pop",
  "pop2",
  "dup",
  "dup_x1",
  "dup_x2",
  "dup2",
  "dup2_x1",
  "dup2_x2",
  "swap",
  "iadd",
  "ladd",
  "fadd",
  "dadd",
  "isub",
  "lsub",
  "fsub",
  "dsub",
  "imul",
  "lmul",
  "fmul",
  "dmul",
  "idiv",
  "ldiv",
  "fdiv",
  "ddiv",
  "irem",
  "lrem",
  "frem",
  "drem",
  "ineg",
  "lneg",
  "fneg",
  "dneg",
  "ishl",
  "lshl",
  "ishr",
  "lshr",
  "iushr",
  "lushr",
  "iand",
  "land",
  "ior",
  "lor",
  "ixor",
  "lxor",
  "iinc",
  "i2l",
  "i2f",
  "i2d",
  "l2i",
  "l2f",
  "l2d",
  "f2i",
  "f2l",
  "f2d",
  "d2i",
  "d2l",
  "d2f",
  "i2b",
  "i2c",
  "i2s",
  "lcmp",
  "fcmpl",
  "fcmpg",
  "dcmpl",
  "dcmpg",
  "ifeq",
  "ifne",
  "iflt",
  "ifge",
  "ifgt",
  "ifle",
  "if_icmpeq",
  "if_icmpne",
  "if_icmplt",
  "if_icmpge",
  "if_icmpgt",
  "if_icmple",
  "if_acmpeq",
  "if_acmpne",
  "goto",
  "jsr",
  "ret",
  "tableswitch",
  "lookupswitch",
  "ireturn",
  "lreturn",
  "freturn",
  "dreturn",
  "areturn",
  "return",
  "getstatic",
  "putstatic",
  "getfield",
  "putfield",
  "invokevirtual",
  "invokespecial",
  "invokestatic",
  "invokeinterface",
  "invokedynamic",
  "new",
  "newarray",
  "anewarray",
  "arraylength",
  "athrow",
  "checkcast",
  "instanceof",
  "monitorenter",
  "monitorexit",
  "wide",
  "multianewarray",
  "ifnull",
  "ifnonnull",
  "goto_w",
  "jsr_w",
  "breakpoint",
] as const;

const LOCAL_OPERAND_OPCODES = new Set([
  0x15, 0x16, 0x17, 0x18, 0x19, 0x36, 0x37, 0x38, 0x39, 0x3a, 0xa9,
]);
const CP1_OPCODES = new Set([0x12]);
const CP2_OPCODES = new Set([
  0x13, 0x14, 0xb2, 0xb3, 0xb4, 0xb5, 0xb6, 0xb7, 0xb8, 0xbb, 0xbd, 0xc0,
  0xc1,
]);
const BRANCH2_OPCODES = new Set([
  0x99, 0x9a, 0x9b, 0x9c, 0x9d, 0x9e, 0x9f, 0xa0, 0xa1, 0xa2, 0xa3, 0xa4,
  0xa5, 0xa6, 0xa7, 0xa8, 0xc6, 0xc7,
]);

function readSigned16(bytes: Uint8Array, offset: number) {
  const value = (bytes[offset] << 8) | bytes[offset + 1];
  return value & 0x8000 ? value - 0x10000 : value;
}

function readSigned32(bytes: Uint8Array, offset: number) {
  return new DataView(
    bytes.buffer,
    bytes.byteOffset + offset,
    4,
  ).getInt32(0, false);
}

function disassembleBytecode(
  code: Uint8Array,
  constantLabel: (index: number) => string,
  constantLiteral: (index: number) => string | number | undefined,
) {
  const instructions: BytecodeInstruction[] = [];
  let cursor = 0;
  let truncated = false;
  const canRead = (count: number) => cursor + count <= code.length;
  const u1 = () => code[cursor++];
  const u2 = () => {
    const value = (code[cursor] << 8) | code[cursor + 1];
    cursor += 2;
    return value;
  };
  const s1 = () => {
    const value = u1();
    return value & 0x80 ? value - 0x100 : value;
  };
  const s2 = () => {
    const value = readSigned16(code, cursor);
    cursor += 2;
    return value;
  };
  const s4 = () => {
    const value = readSigned32(code, cursor);
    cursor += 4;
    return value;
  };

  while (cursor < code.length) {
    if (instructions.length >= 2_000) {
      truncated = true;
      break;
    }
    const offset = cursor;
    const opcode = u1();
    const mnemonic = OPCODE_NAMES[opcode] ?? `reserved_${opcode.toString(16)}`;
    let operands = "";
    let reference: string | undefined;
    let literal: string | number | undefined;

    try {
      if (opcode === 0x10 && canRead(1)) {
        operands = String(s1());
      } else if (opcode === 0x11 && canRead(2)) {
        operands = String(s2());
      } else if (CP1_OPCODES.has(opcode) && canRead(1)) {
        const index = u1();
        operands = `#${index}`;
        reference = constantLabel(index);
        literal = constantLiteral(index);
      } else if (CP2_OPCODES.has(opcode) && canRead(2)) {
        const index = u2();
        operands = `#${index}`;
        reference = constantLabel(index);
        literal = constantLiteral(index);
      } else if (LOCAL_OPERAND_OPCODES.has(opcode) && canRead(1)) {
        operands = `local ${u1()}`;
      } else if (BRANCH2_OPCODES.has(opcode) && canRead(2)) {
        operands = `${offset + s2()}`;
      } else if (opcode === 0x84 && canRead(2)) {
        operands = `local ${u1()}, ${s1()}`;
      } else if (opcode === 0xaa) {
        while (cursor % 4 !== 0 && cursor < code.length) cursor += 1;
        if (!canRead(12)) throw new Error("Truncated tableswitch");
        const fallback = offset + s4();
        const low = s4();
        const high = s4();
        const count = high - low + 1;
        if (count < 0 || count > 4_096 || !canRead(count * 4)) {
          throw new Error("Invalid tableswitch");
        }
        const targets: string[] = [];
        for (let index = 0; index < count; index += 1) {
          targets.push(`${low + index}:${offset + s4()}`);
        }
        operands = `${targets.join(", ")} default:${fallback}`;
      } else if (opcode === 0xab) {
        while (cursor % 4 !== 0 && cursor < code.length) cursor += 1;
        if (!canRead(8)) throw new Error("Truncated lookupswitch");
        const fallback = offset + s4();
        const pairs = s4();
        if (pairs < 0 || pairs > 4_096 || !canRead(pairs * 8)) {
          throw new Error("Invalid lookupswitch");
        }
        const targets: string[] = [];
        for (let index = 0; index < pairs; index += 1) {
          targets.push(`${s4()}:${offset + s4()}`);
        }
        operands = `${targets.join(", ")} default:${fallback}`;
      } else if (opcode === 0xb9 && canRead(4)) {
        const index = u2();
        const count = u1();
        u1();
        operands = `#${index}, args ${count}`;
        reference = constantLabel(index);
      } else if (opcode === 0xba && canRead(4)) {
        const index = u2();
        u2();
        operands = `#${index}`;
        reference = constantLabel(index);
      } else if (opcode === 0xbc && canRead(1)) {
        const arrayTypes: Record<number, string> = {
          4: "boolean",
          5: "char",
          6: "float",
          7: "double",
          8: "byte",
          9: "short",
          10: "int",
          11: "long",
        };
        const type = u1();
        operands = arrayTypes[type] ?? `type ${type}`;
      } else if (opcode === 0xc4 && canRead(3)) {
        const widenedOpcode = u1();
        const local = u2();
        operands = `${OPCODE_NAMES[widenedOpcode] ?? widenedOpcode} local ${local}`;
        if (widenedOpcode === 0x84 && canRead(2)) operands += `, ${s2()}`;
      } else if (opcode === 0xc5 && canRead(3)) {
        const index = u2();
        operands = `#${index}, dimensions ${u1()}`;
        reference = constantLabel(index);
      } else if ((opcode === 0xc8 || opcode === 0xc9) && canRead(4)) {
        operands = `${offset + s4()}`;
      }
    } catch {
      cursor = code.length;
      truncated = true;
      operands = "<truncated operand>";
    }

    instructions.push({
      offset,
      opcode,
      mnemonic,
      operands,
      reference,
      literal,
    });
  }
  return { instructions, truncated };
}

export function parseClassFile(bytes: Uint8Array, path: string): ClassInfo {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const reader = new Reader(view);
  if (reader.u4() !== 0xcafebabe) throw new Error("Invalid class-file magic");

  reader.u2();
  const majorVersion = reader.u2();
  const poolCount = reader.u2();
  const pool: ConstantPoolEntry[] = new Array(poolCount).fill(null);

  for (let index = 1; index < poolCount; index += 1) {
    const tag = reader.u1();
    switch (tag) {
      case 1: {
        const length = reader.u2();
        pool[index] = { tag, value: decodeModifiedUtf8(reader.bytes(length)) };
        break;
      }
      case 3:
        pool[index] = { tag, value: reader.u4() | 0 };
        break;
      case 4: {
        const bits = reader.u4();
        const floatBuffer = new ArrayBuffer(4);
        const floatView = new DataView(floatBuffer);
        floatView.setUint32(0, bits, false);
        pool[index] = {
          tag,
          value: floatView.getFloat32(0, false),
        };
        break;
      }
      case 5:
      case 6:
        pool[index] = { tag, value: reader.u8() };
        index += 1;
        break;
      case 7:
      case 8:
      case 16:
      case 19:
      case 20:
        pool[index] = { tag, index: reader.u2() };
        break;
      case 9:
      case 10:
      case 11:
      case 12:
      case 17:
      case 18:
        pool[index] = { tag, first: reader.u2(), second: reader.u2() };
        break;
      case 15:
        pool[index] = { tag, kind: reader.u1(), index: reader.u2() };
        break;
      default:
        throw new Error(`Unsupported constant-pool tag ${tag}`);
    }
  }

  const utf8 = (index: number) => {
    const entry = pool[index];
    return entry?.tag === 1 ? entry.value : "";
  };
  const className = (index: number) => {
    const entry = pool[index];
    return entry?.tag === 7 ? utf8(entry.index) : "";
  };
  const memberReference = (entry: ConstantPoolEntry) => {
    if (!entry || ![9, 10, 11].includes(entry.tag)) return "";
    if (!("first" in entry)) return "";
    const owner = className(entry.first);
    const pair = pool[entry.second];
    if (!pair || pair.tag !== 12) return owner;
    return `${owner}.${utf8(pair.first)}${descriptorType(utf8(pair.second))}`;
  };
  const constantLabel = (index: number): string => {
    const entry = pool[index];
    if (!entry) return "";
    if (entry.tag === 1) return entry.value;
    if (entry.tag === 3 || entry.tag === 4) return String(entry.value);
    if (entry.tag === 7) return className(index).replaceAll("/", ".");
    if (entry.tag === 8) return JSON.stringify(utf8(entry.index));
    if ([9, 10, 11].includes(entry.tag)) return memberReference(entry);
    if (entry.tag === 12)
      return `${utf8(entry.first)}${descriptorType(utf8(entry.second))}`;
    if (entry.tag === 16) return descriptorType(utf8(entry.index));
    if (entry.tag === 17 || entry.tag === 18)
      return `bootstrap ${entry.first} · ${constantLabel(entry.second)}`;
    if (entry.tag === 15) {
      const target = pool[entry.index];
      return `handle ${entry.kind} · ${
        target && [9, 10, 11].includes(target.tag)
          ? memberReference(target)
          : constantLabel(entry.index)
      }`;
    }
    return "";
  };
  const constantLiteral = (index: number): string | number | undefined => {
    const entry = pool[index];
    if (!entry) return undefined;
    if (entry.tag === 3 || entry.tag === 4) return entry.value;
    if (entry.tag === 8) return utf8(entry.index);
    return undefined;
  };

  const flags = reader.u2();
  const thisClass = reader.u2();
  const superClass = reader.u2();
  const interfaceCount = reader.u2();
  const interfaces: string[] = [];
  for (let i = 0; i < interfaceCount; i += 1) {
    interfaces.push(className(reader.u2()).replaceAll("/", "."));
  }

  const fieldCount = reader.u2();
  const fields: FieldInfo[] = [];
  for (let index = 0; index < fieldCount; index += 1) {
    const memberFlags = reader.u2();
    const name = utf8(reader.u2());
    const descriptor = descriptorType(utf8(reader.u2()));
    skipAttributes(reader, reader.u2());
    fields.push({
      name,
      descriptor,
      access: accessFlags(memberFlags, MEMBER_ACCESS),
    });
  }

  const methodCount = reader.u2();
  const methods: MethodInfo[] = [];
  let classInstructionCount = 0;
  for (let index = 0; index < methodCount; index += 1) {
    const memberFlags = reader.u2();
    const name = utf8(reader.u2());
    const descriptor = descriptorType(utf8(reader.u2()));
    const attributeCount = reader.u2();
    let codeSize = 0;
    let instructions: BytecodeInstruction[] = [];
    let bytecodeTruncated = false;

    for (let attribute = 0; attribute < attributeCount; attribute += 1) {
      const attributeName = utf8(reader.u2());
      const attributeLength = reader.u4();
      if (
        attributeName !== "Code" ||
        classInstructionCount >= 6_000 ||
        attributeLength < 12
      ) {
        reader.skip(attributeLength);
        continue;
      }

      const attributeBytes = reader.bytes(attributeLength);
      const codeReader = new Reader(
        new DataView(
          attributeBytes.buffer,
          attributeBytes.byteOffset,
          attributeBytes.byteLength,
        ),
      );
      codeReader.u2();
      codeReader.u2();
      codeSize = codeReader.u4();
      const code = codeReader.bytes(codeSize);
      const disassembly = disassembleBytecode(
        code,
        constantLabel,
        constantLiteral,
      );
      instructions = disassembly.instructions.slice(
        0,
        Math.max(0, 6_000 - classInstructionCount),
      );
      classInstructionCount += instructions.length;
      bytecodeTruncated =
        disassembly.truncated ||
        instructions.length < disassembly.instructions.length;
    }

    methods.push({
      name,
      descriptor,
      access: accessFlags(memberFlags, MEMBER_ACCESS),
      codeSize,
      instructions,
      bytecodeTruncated,
    });
  }
  const bootstrapMethods: Array<{
    target: string;
    arguments: Array<string | number>;
  }> = [];
  const classAttributeCount = reader.u2();
  for (let index = 0; index < classAttributeCount; index += 1) {
    const attributeName = utf8(reader.u2());
    const attributeLength = reader.u4();
    const attributeBytes = reader.bytes(attributeLength);
    if (attributeName !== "BootstrapMethods") continue;
    const bootstrapReader = new Reader(
      new DataView(
        attributeBytes.buffer,
        attributeBytes.byteOffset,
        attributeBytes.byteLength,
      ),
    );
    const bootstrapCount = bootstrapReader.u2();
    if (bootstrapCount > 4_096) {
      throw new Error("Bootstrap method count exceeds the bounded parser limit");
    }
    for (let bootstrap = 0; bootstrap < bootstrapCount; bootstrap += 1) {
      const methodHandleIndex = bootstrapReader.u2();
      const argumentCount = bootstrapReader.u2();
      if (argumentCount > 16_384) {
        throw new Error(
          "Bootstrap argument count exceeds the bounded parser limit",
        );
      }
      const argumentsList: Array<string | number> = [];
      for (let argument = 0; argument < argumentCount; argument += 1) {
        const constantIndex = bootstrapReader.u2();
        argumentsList.push(
          constantLiteral(constantIndex) ?? constantLabel(constantIndex),
        );
      }
      bootstrapMethods.push({
        target: constantLabel(methodHandleIndex),
        arguments: argumentsList,
      });
    }
  }

  for (const method of methods) {
    for (const instruction of method.instructions) {
      if (instruction.mnemonic !== "invokedynamic") continue;
      const constantIndex = Number(
        instruction.operands.match(/^#(\d+)/)?.[1] ?? "-1",
      );
      const entry = pool[constantIndex];
      if (!entry || entry.tag !== 18) continue;
      const bootstrap = bootstrapMethods[entry.first];
      if (!bootstrap) continue;
      instruction.bootstrapTarget = bootstrap.target;
      instruction.bootstrapArguments = bootstrap.arguments;
    }
  }

  const internalName = className(thisClass) || path.replace(/\.class$/i, "");
  const dottedName = internalName.replaceAll("/", ".");
  const splitAt = dottedName.lastIndexOf(".");
  const references = Array.from(
    new Set(
      pool
        .map(memberReference)
        .filter(Boolean)
        .map((value) => value.replaceAll("/", ".")),
    ),
  ).slice(0, 600);

  const strings = Array.from(
    new Set(
      pool
        .filter((entry): entry is Extract<ConstantPoolEntry, { tag: 1 }> =>
          Boolean(entry && entry.tag === 1),
        )
        .map((entry) => entry.value)
        .filter((value) => value.length >= 3 && value.length <= 4096),
    ),
  ).slice(0, 1200);

  const suspiciousReferences = references.filter((value) =>
    /Runtime\.exec|ProcessBuilder|java\.net\.|java\.lang\.reflect|ClassLoader|System\.load|java\.awt\.Robot|Clipboard/i.test(
      value,
    ),
  ).length;
  const referencedClasses = Array.from(
    new Set(
      pool
        .filter(
          (
            entry,
          ): entry is {
            tag: 7 | 8 | 16 | 19 | 20;
            index: number;
          } =>
            Boolean(entry && entry.tag === 7),
        )
        .map((entry) => utf8(entry.index))
        .filter((value) => value && !value.startsWith("["))
        .map((value) => value.replaceAll("/", ".")),
    ),
  ).slice(0, 1_000);

  return {
    path,
    name: dottedName,
    packageName: splitAt < 0 ? "(default)" : dottedName.slice(0, splitAt),
    simpleName: splitAt < 0 ? dottedName : dottedName.slice(splitAt + 1),
    superClass: className(superClass).replaceAll("/", "."),
    interfaces,
    majorVersion,
    javaVersion: javaVersion(majorVersion),
    access: accessFlags(flags, CLASS_ACCESS),
    methods,
    fields,
    references,
    referencedClasses,
    strings,
    suspiciousReferences,
  };
}

export function classSkeleton(info: ClassInfo) {
  const classKeyword = info.access.includes("interface")
    ? "interface"
    : info.access.includes("enum")
      ? "enum"
      : "class";
  const modifiers = info.access
    .filter((item) => !["super", "interface", "enum"].includes(item))
    .join(" ");
  const inheritance =
    info.superClass && info.superClass !== "java.lang.Object"
      ? ` extends ${info.superClass}`
      : "";
  const implemented = info.interfaces.length
    ? ` implements ${info.interfaces.join(", ")}`
    : "";
  const fields = info.fields
    .map(
      (field) =>
        `    ${field.access.join(" ")} ${field.descriptor} ${field.name};`.trimEnd(),
    )
    .join("\n");
  const methods = info.methods
    .map(
      (method) =>
        `    ${method.access.join(" ")} ${method.name}${method.descriptor};`.trimEnd(),
    )
    .join("\n");

  return [
    info.packageName !== "(default)" ? `package ${info.packageName};\n` : "",
    `// Structural class view. Use “Decompile with CFR” for method bodies.`,
    `${modifiers} ${classKeyword} ${info.simpleName}${inheritance}${implemented} {`,
    fields,
    fields && methods ? "" : null,
    methods,
    "}",
  ]
    .filter((line) => line !== null)
    .join("\n");
}
